import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Box, CircularProgress, useTheme,
} from '@mui/material';
import { useDashboard } from '../../../contexts/DashboardContext';
import { resolveStructuredPath } from '../../studio/studioV2AppBindingMapper';
import {
  PLUGIN_PANEL_SANDBOX, clampPanelHeight, pluginPanelDocument, safeRunInput, usePluginPanels,
} from '../pluginPanels';

/** The serialized message a plugin frame receives: { type, data, loading, theme }. */
export function usePluginPayload(data, loading = false) {
  const theme = useTheme();
  return useMemo(() => {
    const message = {
      type: 'interviewflow:data',
      data: data ?? null,
      loading: Boolean(loading),
      theme: { mode: theme.palette.mode, primary: theme.palette.primary.main },
    };
    try {
      return JSON.stringify(message);
    } catch {
      return JSON.stringify({ ...message, data: null });
    }
  }, [data, loading, theme.palette.mode, theme.palette.primary.main]);
}

/**
 * Plugin HTML in a sandboxed frame: no same-origin access, no network; links open in a new tab.
 * `onRun(input)` receives validated flow inputs the plugin asks for.
 */
export function SandboxedPluginFrame({
  name, html, height: initialHeight, payload, onRun,
}) {
  const frameRef = useRef(null);
  const loadsRef = useRef(0);
  const [broken, setBroken] = useState(false);
  const [height, setHeight] = useState(() => clampPanelHeight(initialHeight));
  const srcDoc = useMemo(() => pluginPanelDocument(html), [html]);

  useEffect(() => {
    loadsRef.current = 0;
    setBroken(false);
  }, [srcDoc]);

  const post = useCallback(() => {
    const win = frameRef.current?.contentWindow;
    if (!win || broken) return;
    win.postMessage(JSON.parse(payload), '*');
  }, [payload, broken]);

  useEffect(() => { post(); }, [post]);

  useEffect(() => {
    const onMessage = (event) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      const msg = event.data;
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'interviewflow:ready') post();
      else if (msg.type === 'interviewflow:resize') setHeight((h) => clampPanelHeight(msg.height, h));
      else if (msg.type === 'interviewflow:run') {
        const input = safeRunInput(msg.input);
        if (input && onRun) onRun(input);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [post, onRun]);

  const onLoad = () => {
    loadsRef.current += 1;
    if (loadsRef.current > 1) setBroken(true);
    else post();
  };

  if (broken) {
    return (
      <Alert severity="error" sx={{ m: 1 }} data-testid="plugin-panel-blocked">
        This plugin panel tried to open another page, so it was stopped.
      </Alert>
    );
  }

  return (
    <Box
      component="iframe"
      ref={frameRef}
      title={name || 'Plugin panel'}
      sandbox={PLUGIN_PANEL_SANDBOX}
      referrerPolicy="no-referrer"
      srcDoc={srcDoc}
      onLoad={onLoad}
      data-testid="plugin-panel-frame"
      style={{ height }}
      sx={{
        width: '100%', border: 0, display: 'block', bgcolor: 'transparent',
      }}
    />
  );
}

/** The panel's result field from its flow output, else its bound or `outputKey` slice, else the whole output. */
function usePluginPanelData(panelId, outputKey) {
  const { config, getSlice, panelOutputs } = useDashboard();
  const root = panelOutputs?.[panelId] ?? null;
  const panel = config?.panels?.find((p) => String(p.id || p._id) === String(panelId));
  const outputPath = String(panel?.source?.outputPath || '').trim();
  const hasBindings = Array.isArray(panel?.source?.bindings) && panel.source.bindings.length > 0;
  const byPath = outputPath && !hasBindings ? resolveStructuredPath(root, outputPath) : undefined;
  if (byPath !== undefined) return byPath;
  const sliced = getSlice ? getSlice(panelId, outputKey) : null;
  return sliced ?? root;
}

function PluginPanelBody({ plugin, panelId, outputKey }) {
  const { runPanelFlow, loadingPanelId } = useDashboard();
  const data = usePluginPanelData(panelId, outputKey || plugin.outputKey);
  const loading = loadingPanelId === panelId;
  const payload = usePluginPayload(data, loading);
  const onRun = useCallback((input) => {
    if (runPanelFlow && !loading) runPanelFlow(panelId, input);
  }, [runPanelFlow, loading, panelId]);

  return (
    <SandboxedPluginFrame name={plugin.name} html={plugin.html} height={plugin.height} payload={payload} onRun={onRun} />
  );
}

export default function DashboardPluginPanel({ panelId, outputKey, config }) {
  const { config: appConfig } = useDashboard();
  const { panels, loading, error } = usePluginPanels(appConfig?.pluginPanels);
  const ref = config?.pluginPanel;
  const plugin = panels.find((p) => p.ref === ref);

  if (!ref) {
    return <Alert severity="info" sx={{ m: 1 }}>Choose a plugin panel in App Builder.</Alert>;
  }
  if (loading) {
    return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>;
  }
  if (!plugin) {
    return (
      <Alert severity="warning" sx={{ m: 1 }} data-testid="plugin-panel-missing">
        {error
          ? `Couldn't load plugin panels: ${error}`
          : 'This panel comes from a plugin that is no longer installed or enabled in this workspace.'}
      </Alert>
    );
  }
  return <PluginPanelBody plugin={plugin} panelId={panelId} outputKey={outputKey} />;
}
